import { COMPANION_PANEL_ID } from '@/components/companion/feed/companion-panel-ids';
import { isEditableTarget } from '@/libs/is-editable-target';
import { useEffect, useRef } from 'react';

/** A field of the page keeps the key; the panel's own composer does not, so ⌘K closes it too. */
const keepsTheShortcut = (target: EventTarget | null): boolean =>
  isEditableTarget(target) &&
  !(target instanceof HTMLElement && target.closest(`#${COMPANION_PANEL_ID}`));

/**
 * ⌘K on macOS, Ctrl+K elsewhere: both are accepted everywhere, since a
 * keyboard does not say which platform it was bought for. Mounted with the
 * panel host rather than the trigger, so the shortcut still works on a screen
 * that hides the trigger. Disabled, it listens to nothing and leaves the key to
 * the page and the browser.
 */
export function useCompanionPanelHotkey(toggle: () => void, enabled = true): void {
  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      if (event.repeat || event.shiftKey || event.altKey || keepsTheShortcut(event.target)) return;
      event.preventDefault();
      toggleRef.current();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
